<template>
	<div class="p-grid">
		<div class="p-col-12">
			<div class="card">
        <Toolbar class="p-mb-4">
          <template #start>
            <div style="font-size: 1.4em; color: inherit" class="p-mr-3">{{ isForkliftTenant ? $t('Equipment') : $t('Vehicles') }}</div>
            <AddNewButton :label="$t('Add')" @click="addNewItem" class="p-mr-2"/>
          </template>
          <template #end>
            <SearchInput :disabled="dataTableIsLoading" @change-processed-search-value="changeProcessedSearchValue" v-model="searchData" @show-spinner="isLoading = true"/>
          </template>
        </Toolbar>
        <Spinner v-if="isLoading"/>
        <DesktopDataTable v-else
                          :dataTableIsLoading="dataTableIsLoading"
                          :showHeader="false"
                          :itemsArrayIsEmpty="itemsArrayIsEmpty"
                          :sort="sort"
                          :showSearchInput="false"
                          :actionButtonsCount="actionButtonsCount"
                          @sort-items="sortItems"
                          :headers="[
              {name: 'car_make', title: $t('Make'), sortable: false},
              {name: 'car_model', title: $t('Model'), sortable: false},
              {name: 'plate_number', isHidden: isForkliftTenant, title: $t('Plate number'), sortable: false},
              {name: isForkliftTenant ? 'serial_number' : 'vin', title: isForkliftTenant ? $t('Serial Number') : $t('VIN number'), sortable: false},
              {name: isForkliftTenant ? 'asset_code' : 'engine_code', title: isForkliftTenant ? $t('Asset Code') : $t('Engine code'), sortable: false},
              {name: 'year', title: $t('Year'), sortable: true},
              {name: 'orders_count', class: 'ti-hummer', width: '70px', sortable: false},
              {name: 'offers_count', class: 'ti-clipboard', width: '70px', sortable: false},
              {name: 'owner', title: $t('Owner'), sortable: false},
              {name: 'creation_location_id', title: $t('creationLocation'), minWidth: '160px', sortable: false},
              {name: 'created_at', title: $t('Created'), sortable: true},
          ]">
          <template v-slot:body>
            <tr v-for="(item, index) of items"
                :key="index"
                :class="{'updated-item': isItemUpdated(item.id)}">
              <td>
                <span v-if="carMakeName(item)">{{ carMakeName(item) }}</span>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <span v-if="carModelName(item)">{{ carModelName(item) }}</span>
                <i v-else class="ti-minus"/>
              </td>
              <td v-if="!isForkliftTenant">
                <span v-if="item.plate_number">{{ item.plate_number }}</span>
                <i v-else class="ti-minus"/>
              </td>
              <td>
                <template v-if="isForkliftTenant">
                  <span v-if="item.serial_number">{{ item.serial_number }}</span>
                  <i v-else class="ti-minus"/>
                </template>
                <template v-else>
                  <span v-if="item.vin">{{ item.vin }}</span>
                  <i v-else class="ti-minus"/>
                </template>
              </td>
              <td>
                <template v-if="isForkliftTenant">
                  <span v-if="item.asset_code">{{ item.asset_code }}</span>
                  <i v-else class="ti-minus"/>
                </template>
                <template v-else>
                  <span v-if="item.engine_code">{{ item.engine_code }}</span>
                  <i v-else class="ti-minus"/>
                </template>
              </td>
              <td>
                <span v-if="item.year">{{ item.year }}</span>
                <i v-else class="ti-minus"/>
              </td>
              <td>
                <span>{{ item.ordersCount ?? 0 }}</span>
              </td>
              <td>
                <span>{{ item.offersCount ?? 0 }}</span>
              </td>
              <td>
                <CustomerData :user="item.customer"/>
              </td>
              <td>
                <span v-if="item.creationLocationName">{{ item.creationLocationName }}</span>
                <i v-else class="ti-minus"/>
              </td>
              <td class="table-date-line">
                <span v-if="item.created_at">
                  {{ formatBy(item.created_at, item.creator) }}
                </span>
                <i v-else class="ti-minus"/>
              </td>
              <td v-show="actionButtonsCount">
                <div class="actions">
                  <router-link :to="{ path: `/vehicles/${item.uid}` }">
                    <ViewButton/>
                  </router-link>
                  <EditButton :disabled="dataTableIsLoading"
                              @click="editItem(item)" />
                  <DeleteButton @click="confirmItemDelete(item)"
                                :disabled="dataTableIsLoading"
                                v-show="$store.getters.isAdminOrHigher"/>
                </div>
              </td>
            </tr>
          </template>
        </DesktopDataTable>

        <OwnPaginator v-show="!itemsArrayIsEmpty"
                      :rows="pagination.perPage"
                      :currentPage="pagination.currentPage - 1"
                      :totalPages="pagination.pageCount"
                      :totalRecords="pagination.totalCount"
                      :rowsPerPageOptions="[5,10,25,50,100,200]"
                      :pageLinkSize="11"/>

        <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

        <CarModal :visible="itemModal"
                  :showCustomerSelector="true"
                  :item="item"
                  @update-items="updateItems"
                  @close="closeItemModal"/>

        <ConfirmDeleteModal :visible="confirmItemDeleteModal"
                            :modalDataIsLoading="modalDataIsLoading"
                            @close="closeConfirmItemDeleteModal"
                            @delete-item="deleteItem"
                            :data="computedItemToDeleteData"/>
			</div>
		</div>
	</div>
</template>

<script>
import httpClient from '@/services/http.services'
import httpMixins from "@/mixins/httpMixins";
import datatableMixins from "@/mixins/datatableMixins";
import CarModal from "@/pages/vehicles/components/CarModal";
import formatMixins from "@/mixins/formatMixins";
import settings from "@/settings";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import CustomerData from "@/components/CustomerData.vue";

export default {
  mixins: [ httpMixins, datatableMixins, formatMixins, showErrorsMixins ],
  components: {CustomerData, CarModal },
	data() {
		return {
      fieldsExpand: 'creator,ordersCount,offersCount,customer.customerStatus,creationLocationName',
      apiLink: 'car',
      deleteItemUrl: 'car/delete',
      routePath: '/vehicles',
      sort: {
        column: 'id',
        direction: '-',
      },
		}
	},
  mounted() {
    this.scrollToTop()
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false
    this.checkRouteQuery()
    this.getPageData()
	},
  methods: {
    getPageData() {
      this.getItems()
    },
    async getItems() {
      if (!this.userCanGetData || this.fetchInFlight) return
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true
      this.disableEditButton = true
      const ctrl = this.setController?.('vehicles')
      try {
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          // method: 'post',
          params: {
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            // search: this.searchProcessed ? this.searchData : null,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand,
          },
          signal: ctrl?.signal,
          // data: {}
        })

        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          this.items = data

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false
        this.disableEditButton = false
        if (this._abortors?.vehicles === ctrl) {
          this._abortors.vehicles = null
        }
      }
    },
	},
  computed: {
    isForkliftTenant() {
      return this.$store.getters.isForkliftTenant
    },
    canDelete() {
      return this.$store.getters.isAdminOrHigher
    },
    actionButtonsCount() {
      if (this.canDelete) {
        return 3
      } else {
        return 2
      }
    },
    computedItemToDeleteData() {
      if (!this.itemToDelete) return null
      return this.formatCarLabel(this.itemToDelete)
    },
  }
}
</script>

<style scoped lang="scss">

</style>